import { PenLine, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export function WritingsSection({ writings, loading, emptyMessage }) {
    // Show all writings (scrollable)
    const displayWritings = writings;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <section 
            id="writings" 
            className="py-16 sm:py-24 bg-muted/30"
            data-testid="writings-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="mb-12 sm:mb-16">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-primary flex items-center justify-center">
                            <PenLine className="h-4 w-4 text-primary-foreground" />
                        </div>
                        <h2 
                            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                            data-testid="writings-title"
                        >
                            Writings
                        </h2>
                    </div>
                    
                    {/* Quirky message - always shown at top */}
                    <p 
                        className="text-muted-foreground italic text-lg"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        data-testid="writings-quirky-message"
                    >
                        {emptyMessage}
                    </p>
                </div>

                {/* Scrollable Writings Grid - 3 tiles */}
                <div className="max-h-[600px] overflow-y-auto pr-2 scrollbar-thin">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {Array(3).fill(0).map((_, i) => (
                                <div key={i} className="retro-card p-6">
                                    <div className="h-32 w-full bg-muted skeleton-pulse mb-4" />
                                    <div className="h-6 w-3/4 bg-muted skeleton-pulse mb-3" />
                                    <div className="h-4 w-full bg-muted skeleton-pulse mb-2" />
                                    <div className="h-4 w-2/3 bg-muted skeleton-pulse mb-4" />
                                    <div className="h-3 w-24 bg-muted skeleton-pulse" />
                                </div>
                            ))}
                        </div>
                    ) : displayWritings.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {displayWritings.map((writing) => (
                                <Link 
                                    key={writing.id}
                                    to={`/writing/${writing.id}`}
                                    className="block"
                                >
                                    <article 
                                        className="retro-card group p-0 hover:border-primary transition-all cursor-pointer h-full overflow-hidden"
                                        data-testid={`writing-tile-${writing.id}`}
                                    >
                                        {/* Image */}
                                        {writing.image_url && (
                                            <div className="w-full h-40 border-b-2 border-border overflow-hidden">
                                                <img 
                                                    src={writing.image_url} 
                                                    alt={writing.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            </div>
                                        )}
                                        
                                        <div className="p-6">
                                            <h3 
                                                className="text-lg sm:text-xl tracking-tight mb-3 group-hover:text-primary transition-colors"
                                                style={{ fontFamily: "'Silkscreen', cursive" }}
                                            >
                                                {writing.title}
                                            </h3>
                                            <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                                                {writing.excerpt}
                                            </p>
                                            <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-widest">
                                                <span>{formatDate(writing.created_at)}</span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {writing.read_time}
                                                </span>
                                            </div>
                                            <div className="mt-4 flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="text-xs uppercase tracking-widest">Read More</span>
                                                <ArrowRight className="h-4 w-4" />
                                            </div>
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="retro-card p-12 text-center">
                            <PenLine className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                            <p className="text-muted-foreground text-sm">
                                No writings yet. Check back soon!
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
